import { IResponse } from "../../_services/interface/user.interface";
import { AUTOMATIONS_PATH } from "./constants";
import { IClientDataResponse } from "../../_services/interface/client.interface";
import { hasCredentialsModule } from "../../utils/credentialsModule";
import { checkCollaboratorPermission } from "../../pages/Users/utils/checkPermission";
import {
  destinoDeAutomacoesNoMenu,
  getVisibleJurisdictions,
  hasAnyJurisdictionAccess,
  podeVerAlertas,
} from "../../routes/routeAccess";
import {
  CADIN_FEDERAL_SLUG,
  subpaginasDoCliente,
  TAX_STATUS_SLUG,
} from "../../pages/TaxCurrentAccount/contaCorrenteSlugs";

export interface ISearchDestination {
  id: string;
  title: string;
  /** Palavras que o usuário pode digitar e que não estão no título ("notas", "DTE"). */
  keywords: string;
  path: string;
}

interface IDestinationContext {
  validation: IResponse;
  currentClient: IClientDataResponse | undefined;
  hasCpfProduct: boolean;
  hasTaxCurrentAccountProduct: boolean;
  hasCertificate: boolean;
  /** GEN-4021: gate de jurisdição da sidebar — o colaborador e os resources do cliente */
  collaborator?: IClientDataResponse;
  /** GEN-4021: `null` = resources ainda não carregados (fail-closed) — ver `loadedResources`. */
  resources: string[] | null;
}

/**
 * Destinos do sistema para a busca global (GEN-3497).
 *
 * Metade do valor de um Ctrl/Cmd+K não é achar DADO, é achar TELA — é assim que Linear e
 * Notion o usam. Isso não custa backend nenhum, mas custa disciplina: os gates aqui são os
 * MESMOS da sidebar. Um destino que aparece na busca e não na sidebar leva o usuário a uma
 * tela que o guard de rota vai negar — pior que não oferecer.
 */
export const buildDestinations = (ctx: IDestinationContext): ISearchDestination[] => {
  const { validation, currentClient, hasCpfProduct, hasTaxCurrentAccountProduct, hasCertificate } = ctx;
  const guid = currentClient?.guid_client as string;
  const can = (permission: string) => checkCollaboratorPermission(validation, guid, permission);
  const products = (currentClient?.products ?? {}) as Record<string, boolean>;
  const hasJurisdictionAccess = hasAnyJurisdictionAccess(ctx.collaborator, ctx.resources);
  // GEN-3984: as sub-páginas da Conta Corrente que o cliente contratou (o mesmo gate do submenu)
  const subpaginasContaCorrente = subpaginasDoCliente(
    products,
    getVisibleJurisdictions(ctx.collaborator, ctx.resources)
  );
  // mesmo gate da sidebar (`canShowTaxCurrentAccount`): produto, permissão e acesso a alguma jurisdição
  const podeVerContaCorrente = hasTaxCurrentAccountProduct && can("taxCurrentAccount.read") && hasJurisdictionAccess;
  const temSubpaginaContaCorrente = (slug: string) =>
    podeVerContaCorrente && subpaginasContaCorrente.some((sub) => sub.slug === slug);

  // GEN-4080: o mesmo gate (e a mesma rota) do item "Automações" da sidebar; `null` = esconde.
  const destinoDeAutomacoes = destinoDeAutomacoesNoMenu({ validation, currentClient });

  const all: Array<ISearchDestination & { visible: boolean }> = [
    { id: "dashboard", title: "Dashboard", keywords: "painel indicadores gráficos", path: "/", visible: true },
    {
      id: "notificacoes",
      title: "Notificações",
      keywords: "alertas avisos caixa de entrada",
      path: "/notificacoes",
      visible: true,
    },
    { id: "historico", title: "Meu histórico", keywords: "atividade log", path: "/historico", visible: true },
    {
      id: "tarefas",
      title: "Minhas tarefas",
      keywords: "pendências to-do",
      path: "/tarefas",
      visible: Boolean(products.kanban) && can("kanban.read"),
    },
    {
      id: "kanban",
      title: "Tax Kanban",
      keywords: "obrigações quadro",
      path: "/kanban",
      visible: Boolean(products.kanban) && can("kanban.read"),
    },
    {
      id: "kanban2",
      title: "Kanban 2.0",
      keywords: "quadros cards k2",
      path: "/kanban-v2/quadros",
      visible: Boolean(products.kanban2) && can("kanban2.read"),
    },
    {
      id: "iss",
      title: "Taxly ISS",
      keywords: "notas fiscais serviço",
      path: "/iss",
      visible: can("iss.read") && Boolean(products.iss),
    },
    {
      id: "certidoes",
      title: "Gerenciador de Certidões",
      keywords: "cnd certidão negativa fgts cndt",
      path: "/certidoes",
      visible: hasCertificate && can("cnd.read") && hasJurisdictionAccess,
    },
    {
      id: "caixa-postal",
      title: "Caixa Postal",
      keywords: "dte mensagens domicílio tributário",
      path: "/caixa-postal",
      visible:
        can("dte.read") &&
        Boolean(products.dte_federal || products.dte_state || products.dte_municipal) &&
        hasJurisdictionAccess,
    },
    // GEN-3984: a Conta Corrente é módulo — um destino por sub-página contratada, com o nome do módulo no
    // título (quem busca "conta corrente" acha as duas; um destino só do módulo seria redundante)
    {
      id: "conta-corrente-situacao-fiscal",
      title: "Conta Corrente › Situação Fiscal",
      keywords: "sitfis débitos pendências fiscais receita federal",
      path: "/conta-corrente/situacao-fiscal",
      visible: temSubpaginaContaCorrente(TAX_STATUS_SLUG),
    },
    {
      id: "conta-corrente-cadin-federal",
      title: "Conta Corrente › CADIN Federal",
      keywords: "pgfn inadimplentes registros débitos",
      path: "/conta-corrente/cadin-federal",
      visible: temSubpaginaContaCorrente(CADIN_FEDERAL_SLUG),
    },
    {
      id: "consultas-em-lote",
      title: "Consultas em Lote",
      keywords: "batch massa",
      path: "/consultas-em-lote",
      visible: can("batchCertificatesOnDemand.read") && Boolean(products.batch_certificate_on_demand),
    },
    {
      id: "base-cadastral",
      title: "Base Cadastral",
      keywords: "cadastro registro",
      path: "/base-cadastral",
      visible: can("registry.read") && Boolean(products.registry),
    },
    { id: "empresas", title: "Empresas", keywords: "cnpj filial matriz", path: "/empresas", visible: can("company.read") },
    {
      id: "pessoa-fisica",
      title: "Pessoas Físicas",
      keywords: "cpf persona sócio",
      path: "/pessoa-fisica",
      visible: can("persona.read") && hasCpfProduct,
    },
    { id: "usuarios", title: "Usuários", keywords: "equipe permissões acesso", path: "/usuarios", visible: can("user.read") },
    {
      id: "credenciais",
      title: "Credenciais",
      keywords: "certificado digital senha procuração",
      path: "/credenciais",
      // GEN-4162: a mesma régua da sidebar (catálogo, via `modules.credentials`)
      visible: can("credential.read") && hasCredentialsModule(currentClient, hasTaxCurrentAccountProduct),
    },
    {
      id: "configuracoes",
      title: "Configurações",
      keywords: "ajustes preferências conta",
      path: "/configuracoes",
      visible: can("config.read"),
    },
    {
      id: "alertas",
      title: "Alertas personalizados",
      keywords: "automações regras avisar quando",
      // GEN-4080: a lista de Automações na aba Alertas (o mesmo destino do sino com "+" da barra).
      path: AUTOMATIONS_PATH,
      // `alerts.read`, e não `config.read`: quem administra as regras de alerta é quem tem a
      // permissão de alerta. As duas são papéis distintos no catálogo, e desde que a rota de
      // Configurações deixou de exigir `config.read` (review r5), gatear por ela esconderia o
      // atalho de alguém que alcança a tela e pode ver as regras. GEN-4080 r4: e só com Automações
      // liberado e ligado para o cliente (`podeVerAlertas`), o mesmo gate do sino com "+".
      visible: podeVerAlertas({ validation, currentClient }),
    },
    {
      id: "automacoes",
      title: "Automações",
      keywords: "automação regras agendamento",
      path: destinoDeAutomacoes ?? "/automacoes",
      visible: destinoDeAutomacoes !== null,
    },
  ];

  return all.filter((destination) => destination.visible).map(({ visible, ...rest }) => rest);
};

/**
 * Faixa dos combining marks (acentos) que o NFD separa da letra. Montada por escape ASCII
 * de proposito: o caractere literal ja se perdeu uma vez em arquivo salvo com outra
 * codificacao, e o sintoma e a busca simplesmente parar de ignorar acento.
 */
const COMBINING_MARKS = new RegExp("[\u0300-\u036f]", "g");

/**
 * Casa o termo contra título e palavras-chave, ignorando acento e caixa — quem digita
 * "certidoes" tem que achar "Certidões", senão a busca parece quebrada.
 */
export const normalize = (value: string): string =>
  value
    .normalize("NFD")
    .replace(COMBINING_MARKS, "")
    .toLowerCase()
    .trim();

export const filterDestinations = (destinations: ISearchDestination[], term: string): ISearchDestination[] => {
  const q = normalize(term);
  if (!q) return [];
  return destinations.filter(
    (destination) => normalize(destination.title).includes(q) || normalize(destination.keywords).includes(q)
  );
};
